import type { CompanyWebhookDelivery } from "@freightsimple/generated-hubble-openapi-client/models/CompanyWebhookDelivery";
import type { HubblePublicApiRequestPage } from "@freightsimple/generated-hubble-openapi-client/models/HubblePublicApiRequestPage";
import type { PublicApiRequestAudit } from "@freightsimple/generated-hubble-openapi-client/models/PublicApiRequestAudit";
import { CompanyWebhookDeliveryState } from "@freightsimple/generated-hubble-openapi-client/models/CompanyWebhookDeliveryState";
import { CompanyWebhookDeliveryTrigger } from "@freightsimple/generated-hubble-openapi-client/models/CompanyWebhookDeliveryTrigger";
import { CompanyWebhookEventType } from "@freightsimple/generated-hubble-openapi-client/models/CompanyWebhookEventType";
import { asOffsetDateTime } from "@freightsimple/time/branded-types";

const samplePublicApiRequestAudits: PublicApiRequestAudit[] = [
  {
    auditId: "8f2f6b1a-8f2a-4b1e-9b1a-000000000001",
    shipmentId: "00000000-0000-0000-0000-000000000001",
    companyId: "00000000-0000-0000-0000-000000000002",
    apiKeyId: "00000000-0000-0000-0000-000000000003",
    createdAt: asOffsetDateTime("2026-08-14T15:04:00Z"),
    method: "GET",
    path: "/v1/shipments/get-shipment",
    url: "https://api.freightsimple.com/v1/shipments/get-shipment?shipmentId=00000000-0000-0000-0000-000000000001",
    requestHeaders: JSON.stringify({
      "User-Agent": "AcmeShipper/2.1",
      Accept: "application/json",
    }),
    responseStatus: 200,
    responseBody: JSON.stringify({
      shipmentId: "00000000-0000-0000-0000-000000000001",
      state: "BookingConfirmed",
    }),
    traceId: "00000000-0000-0000-0000-000000000011",
    durationMs: 64,
    userAgent: "AcmeShipper/2.1",
  },
  {
    auditId: "8f2f6b1a-8f2a-4b1e-9b1a-000000000002",
    shipmentId: "00000000-0000-0000-0000-000000000001",
    companyId: "00000000-0000-0000-0000-000000000002",
    apiKeyId: "00000000-0000-0000-0000-000000000003",
    createdAt: asOffsetDateTime("2026-08-14T15:05:00Z"),
    method: "POST",
    path: "/v1/shipments/start-booking",
    url: "https://api.freightsimple.com/v1/shipments/start-booking",
    requestHeaders: JSON.stringify({
      "User-Agent": "AcmeShipper/2.1",
      "Content-Type": "application/json",
    }),
    requestBody: JSON.stringify({
      quoteId: "00000000-0000-0000-0000-000000000004",
      pickupDate: "2026-08-18",
    }),
    responseStatus: 400,
    responseBody: JSON.stringify({
      errorCode: "ShipmentAlreadyBooked",
      errorMessage: "This shipment has already been booked.",
    }),
    traceId: "00000000-0000-0000-0000-000000000012",
    durationMs: 212,
    userAgent: "AcmeShipper/2.1",
  },
];

// A company's log: the shipment's two requests, one of ours that failed, and one logged before
// the request log stored a trace id, duration and user agent.
const companyPublicApiRequests: PublicApiRequestAudit[] = [
  {
    auditId: "audit-invoice-pdf-server-error",
    shipmentId: "00000000-0000-0000-0000-000000000001",
    companyId: "00000000-0000-0000-0000-000000000002",
    apiKeyId: "00000000-0000-0000-0000-000000000003",
    createdAt: asOffsetDateTime("2026-08-14T15:09:00Z"),
    method: "GET",
    path: "/v1/shipments/invoice-pdf",
    url: "https://api.freightsimple.com/v1/shipments/invoice-pdf?shipmentId=00000000-0000-0000-0000-000000000001",
    requestHeaders: JSON.stringify({
      "User-Agent": "AcmeShipper/2.1",
      Accept: "application/pdf",
    }),
    responseStatus: 500,
    responseBody: JSON.stringify({
      errorType: "InternalServerError",
      errorMessage:
        "Please contact api@freightsimple.com and provide the value of the X-Trace-Id header.",
    }),
    traceId: "00000000-0000-0000-0000-000000000013",
    durationMs: 1240,
    userAgent: "AcmeShipper/2.1",
  },
  ...samplePublicApiRequestAudits,
  {
    auditId: "audit-carriers-list-before-trace-ids",
    companyId: "00000000-0000-0000-0000-000000000002",
    apiKeyId: "00000000-0000-0000-0000-000000000003",
    createdAt: asOffsetDateTime("2026-07-02T18:30:00Z"),
    method: "GET",
    path: "/v1/carriers/list",
    url: "https://api.freightsimple.com/v1/carriers/list",
    requestHeaders: JSON.stringify({ Accept: "application/json" }),
    responseStatus: 200,
    responseBody: JSON.stringify([{ carrierIdentifier: "abf" }]),
  },
];

const publicApiRequestPage = {
  requests: companyPublicApiRequests,
  totalCount: 212,
  page: 0,
  pageSize: 25,
  endpoints: [
    "/v1/carriers/list",
    "/v1/saved-commodities/list",
    "/v1/shipments/get-shipment",
    "/v1/shipments/invoice-pdf",
    "/v1/shipments/start-booking",
    "/v1/shipments/start-quoting",
  ],
  retentionMonths: 2,
  bookedShipmentRetentionMonths: 12,
} satisfies HubblePublicApiRequestPage;

export const sampleWebhookDeliveries: CompanyWebhookDelivery[] = [
  {
    companyWebhookDeliveryId: "8f2f6b1a-8f2a-4b1e-9b1a-000000000010",
    companyWebhookId: "00000000-0000-0000-0000-000000000005",
    companyId: "00000000-0000-0000-0000-000000000002",
    shipmentId: "00000000-0000-0000-0000-000000000001",
    eventId: "00000000-0000-0000-0000-00000000e001",
    eventType: CompanyWebhookEventType.ShipmentBooked,
    webhookUrl: "https://acmeshipper.example.com/webhooks/freightsimple",
    requestHeaders: JSON.stringify({
      "X-FreightSimple-Signature": "t=1755185160,v1=1f3d...redacted",
    }),
    requestBody: JSON.stringify({
      event_type: "shipment_booked",
      version: "1.0",
      data: { shipmentId: "00000000-0000-0000-0000-000000000001" },
    }),
    state: CompanyWebhookDeliveryState.Delivered,
    attemptCount: 1,
    httpStatus: 200,
    responseBody: JSON.stringify({ received: true }),
    deliveredAt: asOffsetDateTime("2026-08-14T15:06:01Z"),
    trigger: CompanyWebhookDeliveryTrigger.System,
    createdAt: asOffsetDateTime("2026-08-14T15:06:00Z"),
  },
  {
    companyWebhookDeliveryId: "8f2f6b1a-8f2a-4b1e-9b1a-000000000011",
    companyWebhookId: "00000000-0000-0000-0000-000000000005",
    companyId: "00000000-0000-0000-0000-000000000002",
    shipmentId: "00000000-0000-0000-0000-000000000001",
    eventId: "00000000-0000-0000-0000-00000000e002",
    eventType: CompanyWebhookEventType.ShipmentInTransit,
    webhookUrl: "https://acmeshipper.example.com/webhooks/freightsimple",
    requestHeaders: JSON.stringify({
      "X-FreightSimple-Signature": "t=1755185220,v1=9a7c...redacted",
    }),
    requestBody: JSON.stringify({
      event_type: "shipment_in_transit",
      version: "1.0",
      data: { shipmentId: "00000000-0000-0000-0000-000000000001" },
    }),
    state: CompanyWebhookDeliveryState.Failed,
    attemptCount: 3,
    errorMessage: "SocketTimeoutException: connect timed out",
    trigger: CompanyWebhookDeliveryTrigger.System,
    createdAt: asOffsetDateTime("2026-08-14T15:07:00Z"),
  },
  // Pending and exhausted are written by the retry queue; they are here so the
  // story shows every state the Result badge can take.
  {
    companyWebhookDeliveryId: "8f2f6b1a-8f2a-4b1e-9b1a-000000000012",
    companyWebhookId: "00000000-0000-0000-0000-000000000005",
    companyId: "00000000-0000-0000-0000-000000000002",
    shipmentId: "00000000-0000-0000-0000-000000000001",
    eventId: "00000000-0000-0000-0000-00000000e003",
    eventType: CompanyWebhookEventType.ShipmentDelivered,
    webhookUrl: "https://acmeshipper.example.com/webhooks/freightsimple",
    requestHeaders: JSON.stringify({
      "X-FreightSimple-Signature": "t=1755185280,v1=4b0e...redacted",
    }),
    requestBody: JSON.stringify({
      event_type: "shipment_delivered",
      version: "1.0",
      data: { shipmentId: "00000000-0000-0000-0000-000000000001" },
    }),
    state: CompanyWebhookDeliveryState.Pending,
    attemptCount: 2,
    httpStatus: 503,
    responseBody: "Service Unavailable",
    nextAttemptAt: asOffsetDateTime("2026-08-14T15:13:00Z"),
    trigger: CompanyWebhookDeliveryTrigger.System,
    createdAt: asOffsetDateTime("2026-08-14T15:08:00Z"),
  },
  {
    companyWebhookDeliveryId: "8f2f6b1a-8f2a-4b1e-9b1a-000000000013",
    companyWebhookId: "00000000-0000-0000-0000-000000000005",
    companyId: "00000000-0000-0000-0000-000000000002",
    shipmentId: "00000000-0000-0000-0000-000000000001",
    eventId: "00000000-0000-0000-0000-00000000e004",
    eventType: CompanyWebhookEventType.DocumentUploaded,
    webhookUrl: "https://acmeshipper.example.com/webhooks/freightsimple",
    requestHeaders: JSON.stringify({
      "X-FreightSimple-Signature": "t=1755271600,v1=7d21...redacted",
    }),
    requestBody: JSON.stringify({
      event_type: "shipment_document_uploaded",
      version: "1.0",
      data: { shipmentId: "00000000-0000-0000-0000-000000000001" },
    }),
    state: CompanyWebhookDeliveryState.Exhausted,
    attemptCount: 8,
    httpStatus: 500,
    responseBody: JSON.stringify({ error: "internal error" }),
    trigger: CompanyWebhookDeliveryTrigger.System,
    createdAt: asOffsetDateTime("2026-08-14T15:09:00Z"),
  },
  {
    companyWebhookDeliveryId: "8f2f6b1a-8f2a-4b1e-9b1a-000000000014",
    companyWebhookId: "00000000-0000-0000-0000-000000000005",
    companyId: "00000000-0000-0000-0000-000000000002",
    shipmentId: "00000000-0000-0000-0000-000000000001",
    eventId: "00000000-0000-0000-0000-00000000e005",
    eventType: CompanyWebhookEventType.ShipmentBooked,
    webhookUrl: "https://acmeshipper.example.com/webhooks/freightsimple",
    requestHeaders: JSON.stringify({
      "X-FreightSimple-Signature": "t=1755271000,v1=1b44...redacted",
    }),
    requestBody: JSON.stringify({
      event_type: "shipment_booked",
      version: "1.0",
      data: { shipmentId: "00000000-0000-0000-0000-000000000001" },
    }),
    state: CompanyWebhookDeliveryState.Superseded,
    attemptCount: 2,
    httpStatus: 503,
    responseBody: JSON.stringify({ error: "maintenance" }),
    errorMessage:
      "Not sent again: a newer update for this shipment replaced it",
    trigger: CompanyWebhookDeliveryTrigger.System,
    createdAt: asOffsetDateTime("2026-08-14T14:59:00Z"),
  },
];

export const PUBLIC_API_REQUEST_AUDIT_INLINE_RESPONSES: Record<
  string,
  Record<string, unknown> | unknown[]
> = {
  "/apollo/public-api-requests/list-for-company": publicApiRequestPage,
  "/apollo/public-api-requests/list-for-api-key": publicApiRequestPage,
  // One id, one request: the lookup opens it straight away.
  "/apollo/public-api-requests/list-for-trace-id": [
    companyPublicApiRequests[0]!,
  ],
  "/apollo/shipments/public-api-request-audits-for-shipment":
    samplePublicApiRequestAudits,
  "/apollo/shipments/webhook-deliveries-for-shipment": sampleWebhookDeliveries,
};
